/**
 * The Opulent Restaurant Case Study
 * Beta implementation success story
 */

import { ExtendedCaseStudy } from '@/types/caseStudy';

export const theOpulentCaseStudy: ExtendedCaseStudy = {
  // Metadata
  id: 'the-opulent-2025',
  slug: 'the-opulent-virtual-assistant-success',
  featured: true,
  publishedDate: '2025-08-28',
  category: 'fine-dining',
  tags: ['AI Virtual Assistant', 'Upsell/Cross Sell', 'AI Driven Decisions'],
  
  // Restaurant Info
  restaurantName: 'The Opulent',
  restaurantWebsite: 'https://theopulent.us/',
  location: 'United States',
  cuisineType: 'Fine Dining',
  
  // Hero Section
  heroImage: '/the-opulent-hero.jpg',
  heroTitle: 'How The Opulent Transformed Guest Experience with Dine AI\'s Virtual Assistant',
  heroSubtitle: 'From staffing challenges to seamless service: A beta success story',
  
  // Quick Stats
  keyMetrics: [
    {
      label: 'Staff Time Saved/Week',
      value: '15+ hrs',
      highlight: true
    },
    {
      label: 'Cocktail Upsell Rate',
      value: '+35%',
      highlight: true
    },
    {
      label: 'Customer Wait Time',
      value: '-40%',
      highlight: false
    },
    {
      label: 'Order Accuracy',
      value: '99.8%',
      highlight: false
    }
  ],
  
  // Overview
  overview: {
    title: 'The Virtual Staff Member That Never Calls In Sick',
    content: 'The Opulent sought a solution to staffing challenges but discovered much more—a virtual team member that transformed their entire operation. Owner Dharma Kalakota calls Dine AI "a virtual person I can rely on anytime."'
  },
  
  // Challenges
  challenges: {
    title: 'The Challenges',
    intro: 'Like many fine dining establishments, The Opulent faced modern hospitality hurdles:',
    items: [
      {
        title: 'Ongoing Staffing Issues',
        description: 'Constant recruitment, training, and coverage gaps were draining resources and impacting service quality.',
        icon: 'users'
      },
      {
        title: 'Guest Wait Times',
        description: 'Customers waiting in the lobby had nothing to engage with, leading to perceived longer wait times.',
        icon: 'clock'
      },
      {
        title: 'Order Management Complexity',
        description: 'Servers spent excessive time taking orders and returning to tables for clarifications.',
        icon: 'clipboard'
      },
      {
        title: 'Missed Upselling Opportunities',
        description: 'High-margin cocktails and pairings weren\'t being consistently recommended to guests.',
        icon: 'trending-up'
      }
    ]
  },
  
  // Solution
  solution: {
    title: 'The Dine AI Solution',
    intro: 'The Opulent implemented our Menu Assistant beta across multiple touchpoints:',
    implementation: [
      {
        title: 'Tablet Deployment in Lobby',
        description: 'Guests can explore the menu, learn about dishes, and even pre-order while waiting',
        features: [
          'Interactive menu exploration',
          'Dietary preference filtering',
          'Pre-ordering capability',
          'Entertainment during wait times'
        ],
        icon: 'tablet'
      },
      {
        title: 'Table-Side Quick Scan',
        description: 'Servers can instantly capture orders for entire tables with a simple scan',
        features: [
          'QR code table identification',
          'Automatic order aggregation',
          'Direct Clover POS integration',
          'Real-time order updates'
        ],
        icon: 'qr-code'
      },
      {
        title: 'AI-Powered Recommendations',
        description: 'Smart pairing suggestions that actually increase revenue',
        features: [
          'Cocktail pairing recommendations',
          'Complementary dish suggestions',
          'Personalized to guest preferences',
          'Focus on high-margin items'
        ],
        icon: 'sparkles'
      }
    ]
  },
  
  // Results
  results: {
    title: 'The Results Speak for Themselves',
    intro: 'Within weeks of implementing Dine AI, The Opulent saw dramatic improvements:',
    metrics: [
      {
        metric: 'Reduced Server Trips',
        improvement: '60% fewer',
        description: 'Servers no longer need multiple trips to tables for order clarifications',
        icon: 'activity'
      },
      {
        metric: 'Cocktail Revenue',
        improvement: '+35% increase',
        description: 'AI pairing recommendations drove significant high-margin beverage sales',
        icon: 'dollar-sign'
      },
      {
        metric: 'Customer Engagement',
        improvement: '4.8/5 rating',
        description: 'Guests love the interactive experience while waiting',
        icon: 'star'
      },
      {
        metric: 'Order Processing Time',
        improvement: '3x faster',
        description: 'From table scan to POS entry in under 30 seconds',
        icon: 'zap'
      }
    ]
  },
  
  // Testimonials
  testimonials: [
    {
      quote: "Dine AI is really like a virtual person that I can rely on anytime. We no longer have to constantly deal with ongoing staffing issues. Our servers are more efficient, our guests are happier, and our cocktail sales have never been better.",
      author: "Dharma Kalakota",
      role: "Owner, Opulent Restaurant"
    },
    {
      quote: "The pairing recommendations are so good that guests actually thank us for the suggestions. It's like having a sommelier and cocktail expert at every table.",
      author: "Staff Member",
      role: "The Opulent"
    }
  ],
  
  // Features Used
  featuresUsed: {
    title: 'Dine AI Features Powering The Opulent',
    features: [
      {
        name: 'Menu Assistant',
        description: '24/7 virtual staff member handling guest inquiries and orders',
        icon: 'message-circle'
      },
      {
        name: 'Dine AI Impact Dashboard',
        description: 'Real-time metrics showing revenue impact and operational efficiency',
        icon: 'bar-chart'
      },
      {
        name: 'Customer Preference Insights',
        description: 'Understanding meal journey patterns and preferences',
        icon: 'users'
      },
      {
        name: 'Drink Pairing Intelligence',
        description: 'AI-powered recommendations that increase beverage sales',
        icon: 'wine'
      },
      {
        name: 'Pricing Sweet Spot Analysis',
        description: 'Data-driven pricing optimization for maximum profitability',
        icon: 'target'
      },
      {
        name: 'POS Integration',
        description: 'Seamless connection with Clover for instant order processing',
        icon: 'link'
      }
    ]
  },
  
  // Call to Action
  cta: {
    title: 'Ready to Transform Your Restaurant?',
    subtitle: 'Join The Opulent and other forward-thinking restaurants using Dine AI',
    buttonText: 'Start Your Success Story',
    buttonLink: '/restaurant-menu-search'
  }
};